import { ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TSetShipmentAsReadyToShipMutationInput, useSetShipmentAsReadyToShipMutation } from '../../../generated/graphql';

import { TShipmentType } from '../../../entities/shipments/queries';
import styles from '../SetAsReadyToShipModal/SetAsReadyToShipModal.module.scss';

export type TSetAsReadyToShipModalProps = {
  shipment: TShipmentType;
  onCancel: () => void;
  onSuccess: () => void;
};

type TTrailingTextProps = {
  text: string;
};

const TrailingText = (props: TTrailingTextProps) => {
  const { text } = props;

  return <span className={styles.trailingText}>{text}</span>;
};

export const EditPackageInfoModal = (props: TSetAsReadyToShipModalProps) => {
  const { shipment, onCancel, onSuccess } = props;

  const formFields: TControlledFormComponentProps[] = useMemo(() => {
    return [
      {
        type: 'textField',
        label: 'Package Length',
        name: 'packageInfo.length',
        inputOptions: {
          className: styles.numberInput,
          trailingIcon: <TrailingText text='in' />,
          type: 'number'
        }
      },
      {
        type: 'textField',
        label: 'Package Width',
        name: 'packageInfo.width',
        inputOptions: {
          className: styles.numberInput,
          trailingIcon: <TrailingText text='in' />,
          type: 'number'
        }
      },
      {
        type: 'textField',
        label: 'Package Height',
        name: 'packageInfo.height',
        inputOptions: {
          className: styles.numberInput,
          trailingIcon: <TrailingText text='in' />,
          type: 'number'
        }
      },
      {
        type: 'textField',
        label: 'Package Weight',
        name: 'packageInfo.weight',
        inputOptions: {
          className: styles.numberInput,
          trailingIcon: <TrailingText text='lb' />,
          type: 'number'
        }
      }
    ];
  }, []);

  const schema = useMemo(() => {
    return Joi.object()
      .unknown(false)
      .keys({
        packageInfo: Joi.object().unknown(false).keys({
          length: Joi.number().required(),
          width: Joi.number().required(),
          height: Joi.number().required(),
          weight: Joi.number().required(),
          testing: Joi.string()
        })
      });
  }, []);

  const [setShipmentAsReadyToShip] = useSetShipmentAsReadyToShipMutation();

  const onSubmit = useCallback<TControlledFormProps<TSetShipmentAsReadyToShipMutationInput>['onValidSubmit']>(
    (data) => {
      return setShipmentAsReadyToShip({
        variables: {
          id: shipment.id,
          companyId: shipment.companyId,
          version: shipment._version,
          input: data
        }
      })
        .then(() => {
          toast.success('Package details have been updated.');
          onSuccess();
        })
        .catch((error) => {
          console.error(serializeError(error));
          toast.error("There was an error updating the package's information.");
        });
    },
    [onSuccess, setShipmentAsReadyToShip, shipment]
  );

  return (
    <ModalForm
      closeModal={onCancel}
      confirmLabel='Update'
      contentClassName={styles.setAsReadyToShipModal}
      controlledFormProps={{
        className: styles.form,
        fields: formFields as TControlledFormComponentProps<TSetShipmentAsReadyToShipMutationInput>[],
        formProps: {
          resolver: joiResolver(schema, {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          }),
          defaultValues: {
            packageInfo: {
              length: shipment.packageInfo?.length,
              width: shipment.packageInfo?.width,
              height: shipment.packageInfo?.height,
              weight: shipment.packageInfo?.weight
            }
          }
        },
        onValidSubmit: onSubmit
      }}
      headline='Update Package Details'
      onCancelClick={onCancel}
      size='small'
      supportingText='Update the dimensions for this package.'
    />
  );
};
